import { useMemo } from 'react';
import { useDispatch } from 'react-redux';
import PropTypes from 'prop-types';

import { Box, Typography } from '@material-ui/core';
import { makeStyles } from '@material-ui/styles';
import EditIcon from '@material-ui/icons/Edit';

import { Button, FormGrid, Restricted } from 'components';

import { ROLE_UNDERWRITER } from 'consts';
import { showModal } from 'stores';
import * as utils from 'utils';

import { QuoteLine } from './QuoteLine';

const camelCaseToCapitalCase = (str) => {
  if (!str) return '';
  return str
    .replace(/([A-Z])/g, ' $1') // Add space before capital letters
    .replace(/^./, (char) => char.toUpperCase()) // Capitalize first letter
    .trim(); // Remove leading/trailing spaces
};

const useStyles = makeStyles((theme) => ({
  boldTitle: {
    color: `${theme.palette.neutral.darker}!important`,
    fontWeight: 600,
  },
  warningText: {
    color: `${theme.palette.warning.main}`,
    fontWeight: 400,
    marginTop: theme.spacing(1),
  },
  warningSubtext: {
    color: `${theme.palette.warning.main}`,
    fontWeight: 400,
    fontSize: '0.8em',
    marginTop: theme.spacing(0.25),
  },
  buttonContainer: {
    marginTop: theme.spacing(0.25),
  },
}));

export const QuoteCardPostQuoteOptions = ({ quote, isEndorsement = false, isEditEnabled = false, riskId }) => {
  const dispatch = useDispatch();
  const classes = useStyles();

  const postQuoteOptions = useMemo(() => utils.risk.getPostQuoteOptionsOptions(quote.postQuoteOptions), [quote.postQuoteOptions]);
  const selectedPostQuoteOptionsOptions = useMemo(
    () => utils.risk.getSelectedPostQuoteOptionsOptions(postQuoteOptions),
    [postQuoteOptions]
  );

  const handleEditPostQuoteOptions = ({ postQuoteOptionGroupName }) => {
    dispatch(
      showModal({
        component: 'EDIT_POST_QUOTE_OPTIONS',
        props: {
          title: `Edit ${camelCaseToCapitalCase(postQuoteOptionGroupName)}`,
          fullWidth: true,
          maxWidth: 'md',
          componentProps: {
            quote,
            postQuoteOptionGroupName,
            isEndorsement,
            riskId,
          },
        },
      })
    );
  };

  return (
    Object.keys(selectedPostQuoteOptionsOptions).length > 0 && (
      <>
        {Object.entries(selectedPostQuoteOptionsOptions).map(([postQuoteOptionGroupName, postQuoteOptionGroup]) => (
          <>
            <Box key={postQuoteOptionGroupName} style={{ width: '100%', marginTop: 4 }}>
              <Typography variant="body2" className={classes.warningSubtext}>
                {camelCaseToCapitalCase(postQuoteOptionGroupName)}
              </Typography>
              <FormGrid container spacing={1} style={{ marginTop: 4, marginLeft: 20 }}>
                {Object.entries(postQuoteOptionGroup).map(([postQuoteOptionName, postQuoteOption]) => (
                  <QuoteLine
                    key={postQuoteOptionName}
                    title={camelCaseToCapitalCase(postQuoteOptionName)}
                    value={postQuoteOption.optionName}
                  />
                ))}
              </FormGrid>
            </Box>
            {isEditEnabled && (
              <Restricted exclude={[ROLE_UNDERWRITER]}>
                <FormGrid item xs={12}>
                  <Box display="flex" justifyContent="flex-end" className={classes.buttonContainer}>
                    <Button
                      text={`Edit ${camelCaseToCapitalCase(postQuoteOptionGroupName)}`}
                      onClick={() => handleEditPostQuoteOptions({ postQuoteOptionGroupName })}
                      color="primary"
                      size="xsmall"
                      icon={EditIcon}
                    />
                  </Box>
                </FormGrid>
              </Restricted>
            )}
          </>
        ))}
      </>
    )
  );
};

QuoteCardPostQuoteOptions.propTypes = {
  quote: PropTypes.shape({
    postQuoteOptions: PropTypes.objectOf(
      PropTypes.arrayOf(
        PropTypes.shape({
          selected: PropTypes.bool,
          optionName: PropTypes.string,
        })
      )
    ),
  }).isRequired,
  isEndorsement: PropTypes.bool,
  isEditEnabled: PropTypes.bool,
};

QuoteCardPostQuoteOptions.defaultProps = {
  isEndorsement: false,
  isEditEnabled: false,
};
